import graphql from 'babel-plugin-relay/macro';
import React from 'react';
import {usePaginationFragment, useMutation} from 'react-relay/hooks';
import ConnectionHandler from 'relay-connection-handler-plus';
import useFilters from 'src/hooks/useFilters';
import {showEventNotification} from 'src/utils/notifications';

import Table from '../../../components/table';
import DateCell from '../../../components/table/cell-types/DateCell';
import SecondaryTextCell from '../../../components/table/cell-types/SecondaryTextCell';
import {AssetModelsOrder} from './__generated__/HydrantModelsPaginationQuery.graphql';
import {HydrantModelsTable_hydrantModels$key} from './__generated__/HydrantModelsTable_hydrantModels.graphql';
import {HydrantModelsTableDeleteHydrantModelMutation} from './__generated__/HydrantModelsTableDeleteHydrantModelMutation.graphql';
import {listPagehydrantModelsQuery} from './__generated__/listPagehydrantModelsQuery.graphql';
import TableFilters from './TableFilters';

type Props = {
  hydrantModels: HydrantModelsTable_hydrantModels$key;
};

type Filters = {
  name?: string;
  orderBy?: AssetModelsOrder;
};

function HydrantModelsTable(props: Props) {
  const {data, loadNext, hasNext, refetch} = usePaginationFragment<
    listPagehydrantModelsQuery,
    HydrantModelsTable_hydrantModels$key
  >(
    graphql`
      fragment HydrantModelsTable_hydrantModels on Query
        @argumentDefinitions(
          first: {type: "Int!"}
          after: {type: "String"}
          name: {type: "String"}
          orderBy: {type: "AssetModelsOrder"}
        )
        @refetchable(queryName: "HydrantModelsPaginationQuery") {
        hydrantModels(first: $first, after: $after, orderBy: $orderBy, name: $name)
          @connection(key: "HydrantModelsList_hydrantModels") {
          totalCount
          pageInfo {
            hasPreviousPage
            hasNextPage
            startCursor
            endCursor
          }
          edges {
            node {
              id
              createdAt
              name
              family {
                id
                name
              }
              manufacturer {
                id
                name
              }
            }
          }
        }
      }
    `,
    props.hydrantModels,
  );

  const [commit] = useMutation<HydrantModelsTableDeleteHydrantModelMutation>(
    deleteHydrantModelMutation,
  );

  const {addFilters, setFilters, isTransitioning} = useFilters<Filters>({}, refetch);

  return (
    <Table
      filters={() => (
        <TableFilters onApplyFilters={handleOnApplyFilter} onResetFilters={handleOnResetFilter} />
      )}
      actions={{show: false, delete: true, edit: true, create: true}}
      totalDataLength={data.hydrantModels.totalCount}
      columns={columns}
      isTransitioning={isTransitioning}
      onPaginate={() => hasNext && loadNext(50)}
      onSort={handleOnSort}
      onDelete={handleDelete}
      data={data.hydrantModels.edges}
    />
  );

  function handleDelete(id: string) {
    commit({
      variables: {input: {hydrantModelId: id}},
      onCompleted: payload => {
        const entry = payload.deleteHydrantModel?.hydrantModel;

        if (entry) {
          showEventNotification('removed', 'Model', entry.name);
        }
      },
      updater: store => {
        const root = store.getRoot();

        const connections = ConnectionHandler.getConnections(
          root,
          'HydrantModelsList_hydrantModels',
        );

        if (!connections) {
          return;
        }

        connections.forEach(connection => {
          ConnectionHandler.deleteNode(connection, id);
        });
      },
    });
  }

  function handleOnApplyFilter(filters: {text: string}) {
    addFilters({name: filters.text});
  }

  function handleOnResetFilter() {
    setFilters({});
  }

  async function handleOnSort(sortByConfig: any[]) {
    if (!sortByConfig.length) {
      addFilters({orderBy: undefined});

      return;
    }

    const orderByDirection = sortByConfig[0].desc ? 'DESC' : 'ASC';
    const orderByField = sortByConfig[0].id.toUpperCase();

    addFilters({orderBy: {direction: orderByDirection, field: orderByField}});
  }
}

export default HydrantModelsTable;

const deleteHydrantModelMutation = graphql`
  mutation HydrantModelsTableDeleteHydrantModelMutation($input: DeleteHydrantModelInput!) {
    deleteHydrantModel(input: $input) {
      hydrantModel {
        id
        name
      }
      errors {
        code
        message
      }
    }
  }
`;

const columns = [
  {
    Header: 'Name',
    accessor: 'node.name',
    id: 'name',
    Cell({cell: {value}}: any) {
      return <SecondaryTextCell value={value} />;
    },
  },
  {
    Header: 'Created On',
    accessor: 'node.createdAt',
    id: 'created_at',
    Cell({cell: {value}}: any) {
      return <DateCell value={value} />;
    },
  },
  {
    Header: 'Manufacturer',
    accessor: 'node.manufacturer.name',
    id: 'manufacturer_name',
    Cell({cell: {value}}: any) {
      return <SecondaryTextCell value={value} />;
    },
  },
  {
    Header: 'Type',
    accessor: 'node.family.name',
    id: 'family_name',
    Cell({cell: {value}}: any) {
      return <SecondaryTextCell value={value} />;
    },
  },
];
